import { useTranslate } from '@tolgee/react';
import { useMemo } from 'react';
import classNames from 'classnames';
import { DocumentCategoryFragment } from '@amf/shared/types/graphql';
import Container from '@amf/shared/components/layout/Container';
import { GetStaticProps } from 'next';
import { sdk } from '@amf/shared/lib/graphQlClient';
import { getNavigation, getPartners, NavigationProps } from '@amf/shared/utils/navigation';
import { getLanguageLocale } from '@amf/shared/utils/languages';

export interface DocumentsPageProps extends NavigationProps {
  documents: DocumentCategoryFragment[];
}

type Props = {
  documents: DocumentCategoryFragment[];
};

function DocumentsPage({ documents: rawDocuments }: Props) {
  const { t } = useTranslate();
  const documents = useMemo<DocumentCategoryFragment[]>(
    () =>
      rawDocuments.filter(
        document => document.documents.filter(d => d.files.length > 0).length > 0,
      ),
    [rawDocuments],
  );
  return (
    <Container>
      <h1>{t('documents.title')}</h1>
      <p>{t('documents.info')}</p>
      {documents.map(({ id, name, documents }) => (
        <div key={id}>
          <h2>{name}</h2>
          <div className='DocumentsPage__documentList'>
            {documents.map(({ id, name, description, files }) => (
              <div key={id} className='DocumentsPage__document'>
                <h3>{name}</h3>
                {description && <p>{description}</p>}
                {files.map(({ id, url, icon, name }) => (
                  <a key={id} href={url} download>
                    <i className={classNames('bi', icon)} />
                    {name}
                  </a>
                ))}
              </div>
            ))}
          </div>
        </div>
      ))}
    </Container>
  );
}

export default DocumentsPage;

export const getStaticProps =
  (): GetStaticProps<DocumentsPageProps> =>
  async ({ locale }) => {
    const { documentCategoryList: documents } = await sdk.DocumentCategoryList({
      union: process.env.NEXT_PUBLIC_UNION,
    });

    return {
      props: {
        documents,
        navigation: await getNavigation(getLanguageLocale(locale!)),
        partners: await getPartners(),
      },
      revalidate: 10,
    };
  };
